/* eslint-disable no-underscore-dangle */
import React, {
  useState, useMemo, useCallback,
} from 'react';
import styled from 'styled-components';
import { DEFAULT_THEME_COLOR } from 'appenv';
import { useSelector } from 'react-redux';
import PopupButton from 'components/PopupButton';
import ColoredMaterialIcon, { ColoredMaterialIconProps } from 'components/twilio/components/styled/ColoredMaterialIcon';
import { MoreHoriz } from '@material-ui/icons';

import { selectLocale } from 'models/localization';
import { Booth, DesignCanvasLayer, DesignElementKind } from 'models/cms/booths';
import { selectXtraEvent } from 'models/cms/events';
import MobileBoothBannerSlider from 'pages/booths/templates/MobileProfile/MobileSlider';

import { LiveStreamBoothChannel, liveStreamSelectors, LiveStreamType } from 'models/liveStream';
import PictureInPictureBoothLiveStream from 'components/pictureInPicture/PictureInPictureBoothLiveStream';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import MobileProfileModal from 'components/booth/MobileProfileModal';

const FlexContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding-bottom: 20px;
  position: relative;
`;

const LogoContainer = styled.div`
  width: 100%;
  margin: 16px auto;
  text-align: center;
`;

const NameText = styled.div`
  font-size: 18px;
  line-height: 18px;
  margin: 0 16px 8px;
  text-align: center;
`;

const OverflowBlocker = styled.div`
  width: 17px;
  background-color: #FEFEFE;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
`;

const OverflowBlockerRight = styled(OverflowBlocker)`
  right: 0;
  left: auto;
`;

const StyledLiveStream = styled(PictureInPictureBoothLiveStream)`
  max-height: 250px;
`;

const isDesignElementSupported = (asset: DesignCanvasLayer, locale: string) => {
  if (!asset || asset.__typename !== 'BoothDesignContentAreaElement') return false;
  switch (asset.kind) {
    case DesignElementKind.Image: return !!asset.image[locale]?.originalImageUrl;
    case DesignElementKind.PDF: return !!(asset.pdf[locale]?.fileUrl || asset.thumbnailImage[locale]?.originalImageUrl);
    case DesignElementKind.Video: return !!asset.video[locale]?.playbackUrl;
    case DesignElementKind.Richtext:
    default: return false;
  }
};

const iconProps: ColoredMaterialIconProps = {
  Icon: MoreHoriz,
  color: 'white',
  overrides: {
    height: '50px',
    width: '50px',
    margin: '5px',
  },
};

const MobileProfile = ({ booth }: { booth: Booth }) => {
  const locale = useSelector(selectLocale);
  const { themeColor } = useSelector((s: any) => s.eventConfigs);

  const xtraEvent = useSelector(selectXtraEvent);

  const resolvedColor = themeColor || DEFAULT_THEME_COLOR;
  const [modalOpen, setModalOpen] = useState(false);
  const bannerFiles = useMemo(() => {
    const banners = (booth?.boothDesignCanvas?.layers || [])
      .filter((it) => isDesignElementSupported(it, locale));
    return !xtraEvent?.features.includes('REVERSED_LAYER_ORDER') ? banners.reverse() : banners;
  }, [booth?.boothDesignCanvas?.layers, locale, xtraEvent?.features]);

  const SelectedLogoContainer = (
    <>
      <LogoContainer>
        <MobileBoothBannerSlider assets={bannerFiles} />
      </LogoContainer>
      <OverflowBlocker />
      <OverflowBlockerRight />
    </>
  );

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);
  const liveStreamingChannel = useSelector(liveStreamSelectors.selectCurrentChannel);

  const hideInContentLiveStream = useMediaQuery('(min-width: 960px)');

  return (
    <>
      {
        !hideInContentLiveStream && liveStreamingChannel?.type === LiveStreamType.Booth
        && liveStreamingChannel.data.boothId === booth.id && (
          <StyledLiveStream
            boothId={(liveStreamingChannel as LiveStreamBoothChannel).data.boothId as string}
            boothName={(liveStreamingChannel as LiveStreamBoothChannel).data.boothName}
          />
        )
      }
      <FlexContainer>
        {SelectedLogoContainer}
        <NameText>{booth.name[locale]}</NameText>
      </FlexContainer>
      {!window.location.pathname.startsWith('/preview') && (
        <PopupButton onClick={openModal} color={resolvedColor}>
          <ColoredMaterialIcon {...iconProps} />
        </PopupButton>
      )}
      <MobileProfileModal
        booth={booth}
        modalOpen={modalOpen}
        closeModal={closeModal}
      />
    </>
  );
};

export default MobileProfile;
